import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { getStorageItem } from 'utils/storage';

import { FlowKey } from './shared/types';
import { getFlowChoice, resolveFlowEligibility, resolveRedirect } from './shared/eligibility';
import { FlowLoading, FlowNoAccess, FlowShellHeader } from './shared/FlowLoading';

// Guarda-chuva da apreciação de Realização Destaque (ELE-02): resolve a elegibilidade do
// gestor logado e redireciona para o fluxo correspondente — /diretoria-rede (Normal) ou
// /mad. Elegível a ambos respeita a última escolha; a nenhum, exibe a mensagem de acesso
// negado. O corpo monolítico anterior foi separado em normal/ (FluxoNormal) e mad/
// (FluxoMAD); o histórico permanece no git.
//
// Estados (ELE-02): 'loading' enquanto a elegibilidade não resolve (FlowLoading — o mesmo
// shell que os fluxos mostram, então gate → fluxo é um loading contínuo, sem "pisca");
// 'error' quando a chamada falha (rede/servidor) — distinto de 'no-access', que é a
// elegibilidade resolvida SEM nenhum fluxo. O erro é recuperável ("Tentar novamente").
type GateState = 'loading' | 'error' | 'no-access' | FlowKey;

export function ApreciacaoFlowGate() {
  const { user, cycleSelected } = useAuth();
  const [state, setState] = useState<GateState>('loading');
  // Incrementado pelo botão "Tentar novamente" para re-disparar a resolução após erro.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function decide() {
      if (!user?._id) return;

      setState('loading');

      // Lateralidade/substituição: a home grava manager_lateralidade antes de navegar
      // para cá — o gate roteia pela elegibilidade do gestor SUBSTITUÍDO (mesma regra
      // dos fluxos, que resolvem internalUser pela mesma chave de storage).
      const lateralId = getStorageItem('manager_lateralidade');
      const playerId = typeof lateralId === 'string' && lateralId.length ? lateralId : user._id;

      // A elegibilidade é por CICLO, e o ciclo em vigor é o que o gestor deixou
      // selecionado (o AuthContext o mantém entre as telas). Ainda indefinido, o backend
      // responde pelo ciclo corrente — e a página de destino refaz a conta assim que o
      // ciclo resolve, desviando de fluxo se for o caso (useFlowEligibility).
      const eligibility = await resolveFlowEligibility(playerId, { cycleId: cycleSelected?._id });

      if (cancelled) return;

      // null = não foi possível determinar a elegibilidade (falha de rede/servidor) —
      // NÃO é "sem acesso". Sem acesso é a elegibilidade resolvida sem nenhum fluxo.
      if (!eligibility) {
        setState('error');
        return;
      }

      const target = resolveRedirect(eligibility, getFlowChoice());
      setState(target ?? 'no-access');
    }

    void decide();

    return () => {
      cancelled = true;
    };
  }, [user?._id, cycleSelected?._id, reloadToken]);

  if (state === 'loading') {
    return <FlowLoading />;
  }

  if (state === 'error') {
    return (
      <div className="mx-auto container p-4 lg:p-10">
        <FlowShellHeader />

        <div className="border-2 border-electric-blue rounded-3xl p-8 bg-oxford-blue text-center">
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block mb-2">
            Não foi possível carregar suas realizações.
          </p>
          <p tabIndex={0} className="text-white font-light block mb-6">
            Verifique sua conexão e tente novamente.
          </p>
          <button
            className="text-alice-blue bg-slate-blue text-sm font-semibold uppercase ring-2 ring-slate-blue hover:ring hover:ring-white rounded px-6 h-10"
            onClick={() => setReloadToken(token => token + 1)}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (state === 'no-access') {
    return <FlowNoAccess />;
  }

  return <Navigate to={state} replace />;
}
